import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { CloseButton, Divider, Flex, Text, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';

import { useAppHeight, useIsMobile } from '@izimi/hooks';
import { hex2rgba } from '@izimi/utils';

import { CustomIcon } from '../icon';
import { useModalStore } from '../modals';
import MotionBox from '../MotionBox/MotionBox';

type Props = {
  children: ReactNode;
  title: string;
  onClose?: () => void;
  HeaderRight?: ReactNode;
  HeaderLeft?: ReactElement;
  isVisible: boolean;
  sideBarWidth?: number;
  isMinimized?: boolean;
  toggleMinimize?: () => void;
} & ComponentProps<typeof MotionBox>;

const SideBar = ({
  children,
  isVisible,
  title,
  HeaderRight,
  HeaderLeft,
  sideBarWidth = 400,
  isMinimized,
  toggleMinimize,
  onClose,
  ...props
}: Props) => {
  const appHeight = useAppHeight();
  const theme = useTheme();
  const width = useBreakpointValue(
    {
      base: '90%',
      md: `${sideBarWidth}px`,
      xl: '450px',
    },
    { ssr: false },
  );
  const { modalEntity } = useModalStore();
  const isMobile = useIsMobile();
  const shouldDisableScroll = modalEntity ? false : isMobile;

  return (
    <AnimatePresence>
      <Flex flexDirection='row-reverse'>
        {isVisible && !isMinimized && (
          <RemoveScroll enabled={shouldDisableScroll}>
            <MotionBox
              initial={{ width: 0 }}
              animate={{ width }}
              exit={{ width: 0 }}
              position={{ base: 'fixed', lg: 'relative' }}
              right={0}
              top={0}
              zIndex='modal'
              height={appHeight}
              overflow='auto'
              bg='white'
              borderLeftWidth={1}
              borderColor='neutral100'
              borderLeftRadius={{ base: 'lg', lg: 'none' }}
              as='section'
              display='flex'
              flexDirection='column'
              {...props}>
              <Flex
                alignItems='center'
                justifyContent='space-between'
                p={4}
                pos='sticky'
                top={0}
                zIndex='sticky'
                bg='white'
                minHeight='fit-content'
                flexDir='row'>
                {HeaderLeft}
                <Text
                  as='h2'
                  variant='heading6'
                  color='neutral400'
                  noOfLines={2}
                  textAlign='left'
                  flex='1'
                  title={title}>
                  {title}
                </Text>
                {HeaderRight || (onClose && <CloseButton onClick={onClose} />)}
              </Flex>
              <Divider />
              {children}
            </MotionBox>
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              display={{ base: 'block', lg: 'none' }}
              pos='fixed'
              top={0}
              left={0}
              zIndex='overlay'
              bg={hex2rgba(theme.colors.neutral600, 0.4)}
              w='100%'
              h='100%'
              onClick={onClose}
            />
          </RemoveScroll>
        )}
        {Boolean(toggleMinimize) && (
          <Flex position='relative' width='1.5rem' alignItems='start' justifyContent='center'>
            <Flex
              as='button'
              position='sticky'
              top='1.25rem'
              width='1.5rem'
              height='2rem'
              transform={isMinimized ? undefined : 'translateX(1px)'}
              alignItems='center'
              justifyContent='center'
              borderTop='1px solid'
              borderBottom='1px solid'
              borderLeft='1px solid'
              borderColor='neutral200'
              borderLeftRadius='md'
              bg='white'
              onClick={toggleMinimize}
              zIndex='modal'>
              <CustomIcon
                name={isMinimized ? 'chevron-left' : 'chevron-right'}
                color='neutral400'
                size={24}
                ml='2px'
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </AnimatePresence>
  );
};

export default SideBar;
